/**
 * 결함 WFS·AI 레이어 갱신의 단계별 순수 로직 (DotSolarMap 호출은 마지막에만).
 */
import DotSolarMap from '@/components/map/DotSolarMap';
import { logPipeline } from './logger';
import { PipelinePhase } from './phases';

/**
 * 필터 없고 defectTypes 비어 있을 때 부모 결함 유형만 보충.
 * @param {{ defectFilters: unknown[], defectTypes: unknown[], fetchParentDefectTypes: () => Promise<unknown> }} p
 * @returns {Promise<unknown[]>} 갱신된 또는 기존 defectTypes 배열
 */
export async function ensureDefectTypesForWfs({
  defectFilters,
  defectTypes,
  fetchParentDefectTypes
}) {
  const noFilters = !Array.isArray(defectFilters) || defectFilters.length === 0;
  const typesEmpty = !Array.isArray(defectTypes) || defectTypes.length === 0;
  if (!noFilters || !typesEmpty) {
    return Array.isArray(defectTypes) ? defectTypes : [];
  }

  logPipeline('phase', 'ensure parent defect types for WFS', {
    phase: PipelinePhase.ENSURE_DEFECT_TYPES
  });
  try {
    const data = await fetchParentDefectTypes();
    const raw = data?._embedded?.defectTypes;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw;
    }
  } catch (e) {
    console.warn('[DEFECT-MAP-DEBUG] defectTypes bootstrap in refreshModuleDefectLayer failed:', e);
  }
  return defectTypes || [];
}

/**
 * @param {{ defectFilters: unknown[], defectTypes: unknown[], resolveDefectTypeRowId: (row: unknown) => string|number|null }} p
 * @returns {unknown[]} defect_codes 후보 (숫자 또는 id)
 */
export function buildDefectCodesForWfs({ defectFilters, defectTypes, resolveDefectTypeRowId }) {
  logPipeline('phase', 'build defect_codes list for WFS', {
    phase: PipelinePhase.BUILD_DEFECT_CODES
  });
  const source =
    Array.isArray(defectFilters) && defectFilters.length > 0 ? defectFilters : defectTypes;
  const defectCodes = [];
  for (const dt of source || []) {
    if (dt == null) continue;
    const raw = dt.id != null && dt.id !== '' ? dt.id : resolveDefectTypeRowId(dt);
    if (raw == null || raw === '') continue;
    const n = Number(raw);
    defectCodes.push(Number.isFinite(n) ? n : raw);
  }
  return defectCodes;
}

/**
 * 결함 WFS 한 사이클: 타입 보충 → 스타일 리셋 → 코드 빌드 → 디버그 로그 → 맵 레이어 적용.
 * DotSolarMap 은 이 모듈에서 직접 참조 (단일 진입).
 */
export async function runWfsDefectLayerRefresh({
  defectFilters,
  defectTypes,
  fetchParentDefectTypes,
  resolveDefectTypeRowId,
  resetDefectStyleDebugLogFn,
  getReportId,
  getShowAiDefectFlag,
  setDefectTypes
}) {
  const resolvedTypes = await ensureDefectTypesForWfs({
    defectFilters,
    defectTypes,
    fetchParentDefectTypes
  });
  setDefectTypes(resolvedTypes);

  resetDefectStyleDebugLogFn();

  const defectCodes = buildDefectCodesForWfs({
    defectFilters,
    defectTypes: resolvedTypes,
    resolveDefectTypeRowId
  });

  const dfLen = Array.isArray(defectFilters) ? defectFilters.length : 0;
  const source = dfLen > 0 ? defectFilters : resolvedTypes;
  console.log('[DEFECT-MAP-DEBUG] refreshModuleDefectLayer', {
    reportId: getReportId(),
    useFilters: dfLen > 0,
    defectFiltersLen: dfLen,
    defectTypesLen: resolvedTypes.length,
    sourceLen: (source || []).length,
    defectCodes,
    firstSourceItem: source && source[0] ? { id: source[0].id, seq: source[0].seq, hasLinks: !!source[0]._links } : null
  });

  applyWfsAndAiDefectLayers({
    reportId: getReportId(),
    defectCodes,
    showAiDefect: getShowAiDefectFlag()
  });
}

/**
 * @param {{ reportId: string|number, defectCodes: unknown[], showAiDefect: boolean }} p
 */
export function applyWfsAndAiDefectLayers({ reportId, defectCodes, showAiDefect }) {
  logPipeline('phase', 'DotSolarMap.fetchDefectInfoLocation', {
    phase: PipelinePhase.FETCH_WFS,
    reportId,
    defectCodeCount: defectCodes.length
  });
  DotSolarMap.fetchDefectInfoLocation(reportId, defectCodes);
  if (showAiDefect) {
    logPipeline('phase', 'DotSolarMap.fetchAiDetectDefectLocation', {
      phase: PipelinePhase.FETCH_AI_LAYER,
      reportId
    });
    DotSolarMap.fetchAiDetectDefectLocation(reportId);
  } else {
    DotSolarMap.removeAiDetectDefectLocation();
  }
}
